import { Plus, Trash2 } from "lucide-react";
import { FC, useState } from "react";
import { HubSpotProperty } from "../types/api";
import { VeracrossField } from "../types/veracrossFields";
// import CreateCustomFieldModal, {
//   CustomFieldData,
// } from "./CreateCustomFieldModal";
import DeleteConfirmationModal from "./DeleteConfirmationModal";
import FieldMappingModal from "./FieldMappingModal";
import { Button } from "./ui/button";
import { SearchableSelect } from "./ui/searchable-select";
import { getHubSpotFieldLabel } from "../utils/fieldLabels";
import { apiClient } from "../utils/api";
import { toast } from "../lib/toast";


export interface MappingRow {
  id: string;
  mappingId?: string; // Database ID for existing mappings
  hubspotField: string; // Keep for backward compatibility
  hubspotFieldName: string;
  hubspotFieldLabel: string;
  fieldType: 'string' | 'number' | 'date' | 'boolean' | 'any';
  veracrossField: string; // Keep for backward compatibility
  veracrossFieldName: string; // The actual field name for API calls
  veracrossFieldLabel: string; // The human-readable label for UI display
  default_value?: any;
  transformationType?: string;
}

export interface FieldMappingTableProps {
  title: string;
  description: string;
  category: "applicants" | "students" | "parents" | "applications";
  hubspotProperties: HubSpotProperty[];
  veracrossFields: VeracrossField[];
  mappingRows: MappingRow[];
  onMappingChange: (rows: MappingRow[]) => void;
  onBack?: () => void;
  onNext?: () => void;
  showNotification?: boolean;
  onNotificationClose?: () => void;
  // onCustomFieldCreated?: (fieldData: CustomFieldData) => void;
  nextButtonText?: string;
  nextButtonDisabled?: boolean;
  portalId: number;
  encryptedUserId: string;
}

const FieldMappingTable: FC<FieldMappingTableProps> = ({
  title,
  description,
  category,
  hubspotProperties,
  veracrossFields,
  mappingRows,
  onMappingChange,
  onBack,
  onNext,
  // onCustomFieldCreated,
  nextButtonText = "Next",
  nextButtonDisabled = false,
  portalId,
  encryptedUserId,
}) => {
  const [deleteModalState, setDeleteModalState] = useState<{
    isOpen: boolean;
    rowId: string | null;
    rowName: string | undefined;
  }>({
    isOpen: false,
    rowId: null,
    rowName: undefined,
  });



  // Note: Veracross options are now generated inline for each row to handle
  // filtering of already mapped fields while preserving current selections

  const handleRowChange = (
    id: string,
    field: keyof MappingRow,
    value: string
  ) => {
    const updatedRows = mappingRows.map((row) => {
      if (row.id === id) {
        if (field === 'veracrossField') {
          // When veracrossField changes, also update veracrossFieldName and veracrossFieldLabel
          const selectedField = veracrossFields.find(f => f.veracrossFieldName === value);
          return { 
            ...row, 
            [field]: value,
            veracrossFieldName: selectedField?.veracrossFieldName || value,
            veracrossFieldLabel: selectedField?.veracrossFieldLabel || value
          };
        }
        return { ...row, [field]: value };
      }
      return row;
    });
    onMappingChange(updatedRows);
  };

  const handleMappingCreated = (mapping: {
    hubspotField: string;
    veracrossField: string;
  }) => {
    // Find the corresponding HubSpot property to get the full details
    const hubspotProperty = hubspotProperties.find(
      (prop) => prop.label === mapping.hubspotField
    );

    // Find the corresponding Veracross field to get the full details
    const veracrossField = veracrossFields.find(
      (field) => field.veracrossFieldName === mapping.veracrossField
    );

    // Helper function to map HubSpot property types to expected fieldType values
    const mapHubSpotTypeToFieldType = (type: string): 'string' | 'number' | 'date' | 'boolean' | 'any' => {
      switch (type.toLowerCase()) {
        case 'number':
        case 'enumeration':
          return 'number';
        case 'date':
        case 'datetime':
          return 'date';
        case 'bool':
        case 'boolean':
          return 'boolean';
        default:
          return 'string';
      }
    };

    const newRow: MappingRow = {
      id: Date.now().toString(),
      hubspotField: mapping.hubspotField,
      hubspotFieldName: hubspotProperty?.name || mapping.hubspotField,
      hubspotFieldLabel: getHubSpotFieldLabel(hubspotProperty || { name: mapping.hubspotField }),
      fieldType: hubspotProperty?.type ? mapHubSpotTypeToFieldType(hubspotProperty.type) : 'string',
      veracrossField: mapping.veracrossField,
      veracrossFieldName: veracrossField?.veracrossFieldName || mapping.veracrossField,
      veracrossFieldLabel: veracrossField?.veracrossFieldLabel || mapping.veracrossField,
    };
    onMappingChange([...mappingRows, newRow]);
  };

  const removeRow = async (id: string) => {
    const rowToDelete = mappingRows.find((row) => row.id === id);
    
    // If this is an existing mapping with a database ID, call the API to delete it
    if (rowToDelete?.mappingId && portalId && encryptedUserId) {
      try {
        await apiClient.deleteMapping(portalId, encryptedUserId, rowToDelete.mappingId);
      } catch (error) {
        // Show error toast if API call fails
        toast({
          title: "Error",
          description: "Failed to delete mapping from server. Please try again.",
          variant: "destructive",
        });
        return; // Don't proceed with local removal if API call failed
      }
    }
    
    // Remove from local state
    const updatedRows = mappingRows.filter((row) => row.id !== id);
    onMappingChange(updatedRows);
    
    // Show success notification
    const mappingName = rowToDelete
      ? `${rowToDelete.hubspotFieldLabel || rowToDelete.hubspotField} → ${rowToDelete.veracrossFieldLabel || rowToDelete.veracrossField}`
      : "Mapping";
    
    toast({
      title: "Mapping Removed",
      description: `${mappingName} has been successfully removed.`,
    });
  };

  const handleDeleteClick = (id: string) => {
    const row = mappingRows.find((row) => row.id === id);
    const rowName = row
      ? `${row.hubspotFieldLabel || row.hubspotField} → ${row.veracrossFieldLabel || row.veracrossField}`
      : "this mapping";
    setDeleteModalState({
      isOpen: true,
      rowId: id,
      rowName,
    });
  };

  const handleDeleteConfirm = async () => {
    if (deleteModalState.rowId) {
      await removeRow(deleteModalState.rowId);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModalState({
      isOpen: false,
      rowId: null,
      rowName: undefined,
    });
  };

  const getCategoryLabel = (
    category: "applicants" | "students" | "parents" | "applications"
  ) => {
    const labels = {
      applicants: "Applicants",
      students: "Students",
      parents: "Parents",
      applications: "Applications",
    };
    return labels[category];
  };

  return (
    <div className="w-full">
      {/* Main Content */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">{title}</h2>
        <p className="text-gray-600 mb-6">
          {description}
        </p>

        {/* Mapping Table */}
        <div className="overflow-x-auto overflow-y-visible">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-3 px-4 font-medium text-gray-900">
                  HubSpot field
                </th>
                <th className="text-left py-3 px-4 font-medium text-gray-900">
                  Veracross{" "}
                  {category === "applications"
                    ? "application"
                    : getCategoryLabel(category).toLowerCase()}{" "}
                  field
                </th>
                <th className="text-left py-3 px-4 font-medium text-gray-900">
                  Action
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {mappingRows.map((row) => (
                <tr key={row.id} className="hover:bg-gray-50">
                  <td className="py-3 px-4">
                    <div className="py-2 px-3 text-sm font-medium text-gray-900">
                      {row.hubspotFieldLabel || row.hubspotField || "No field selected"}
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <SearchableSelect
                      options={(() => {
                        // For each row, include the currently selected field in options
                        // even if it's already mapped in other rows
                        const currentFieldName = row.veracrossFieldName || row.veracrossField;
                        const otherMappedFields = new Set(
                          mappingRows
                            .filter(r => r.id !== row.id)
                            .map(r => r.veracrossFieldName || r.veracrossField)
                            .filter(Boolean)
                        );
                        
                        return veracrossFields
                          .filter(field => 
                            !otherMappedFields.has(field.veracrossFieldName) || 
                            field.veracrossFieldName === currentFieldName
                          )
                          .map((field) => ({
                            value: field.veracrossFieldName,
                            label: field.veracrossFieldLabel,
                          }));
                      })()}
                      value={row.veracrossFieldName || row.veracrossField}
                      onValueChange={(value) =>
                        handleRowChange(row.id, "veracrossField", value)
                      }
                      placeholder="Select Veracross field"
                      searchPlaceholder="Search Veracross fields..."
                      className="w-full"
                      showClearButton={false}
                    />
                  </td>
                  <td className="py-3 px-4">
                    <button
                      onClick={() => handleDeleteClick(row.id)}
                      className="text-red-500 hover:text-red-700 p-1"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-between items-center mt-6 pt-6 border-t border-gray-200">
          <div className="flex space-x-3">
            {onBack && (
              <Button variant="outline" onClick={onBack}>
                Back
              </Button>
            )}
            <FieldMappingModal
              hubspotProperties={hubspotProperties}
              veracrossFields={veracrossFields}
              existingMappings={mappingRows}
              onMappingCreated={handleMappingCreated}
              trigger={
                <Button variant="outline">
                  <Plus className="h-4 w-4 mr-2" />
                  Add mapping row
                </Button>
              }
            />
            {/* <CreateCustomFieldModal 
              currentObjectType={category}
              userId={userId}
              onFieldCreated={onCustomFieldCreated} 
            /> */}
          </div>
          {onNext && (
            <Button 
              onClick={onNext} 
              disabled={nextButtonDisabled}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {nextButtonText}
            </Button>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        open={deleteModalState.isOpen}
        onOpenChange={(open) => {
          if (!open) handleDeleteCancel();
        }}
        onConfirm={handleDeleteConfirm}
        title="Delete Field Mapping"
        description="Are you sure you want to delete this field mapping? This action cannot be undone."
      />
    </div>
  );
};

export default FieldMappingTable;
